import React, { useState, useRef, useEffect } from 'react';

interface ResizablePanelsProps {
  left: React.ReactNode;
  right: React.ReactNode;
  /** initial left width in percentage (0-100) */
  initialLeftPercent?: number;
  /** minimum left width in percentage (0-100) */
  minLeftPercent?: number;
  /** maximum left width in percentage (0-100) */
  maxLeftPercent?: number;
}

export const ResizablePanels: React.FC<ResizablePanelsProps> = ({
  left,
  right,
  initialLeftPercent = 50,
  minLeftPercent = 25,
  maxLeftPercent = 75,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftPercent, setLeftPercent] = useState<number>(() => {
    const saved = localStorage.getItem('panel-left-percent');
    const savedValue = saved ? Number(saved) : initialLeftPercent;
    // Ensure saved value is within reasonable bounds
    return Math.min(maxLeftPercent, Math.max(minLeftPercent, savedValue));
  });

  // Save on change
  useEffect(() => {
    localStorage.setItem('panel-left-percent', String(leftPercent));
  }, [leftPercent]);

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const containerWidth = containerRef.current?.getBoundingClientRect().width || 1;

    const onMouseMove = (ev: MouseEvent) => {
      const delta = ev.clientX - startX;
      const newLeft = leftPercent + (delta / containerWidth) * 100;
      const clamped = Math.min(maxLeftPercent, Math.max(minLeftPercent, newLeft));
      setLeftPercent(clamped);
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div ref={containerRef} className="flex flex-1 h-full min-h-0 overflow-hidden">
      <div style={{ width: `${leftPercent}%` }} className="h-full overflow-hidden flex flex-col">
        {left}
      </div>
      {/* Gutter */}
      <div
        className="w-1.5 cursor-col-resize bg-slate-700/50 hover:bg-slate-600/70 transition-colors"
        onMouseDown={startDrag}
      />
      <div className="flex-1 h-full overflow-hidden flex flex-col">
        {right}
      </div>
    </div>
  );
};